/**
 * Lab UserLog → CM Login History (Agent-console parity).
 * CM-API POST /v1/userLog/* proxies hierarchy-service.
 */
import { DEFAULT_PAGE_SIZE } from '@w7/ui'

export type UserLogDeviceApi = {
  device?: { type?: string; brand?: string; model?: string }
  client?: { name?: string; version?: string }
  os?: { name?: string; version?: string; platform?: string }
}

export type UserLogQualityApi = {
  ISP?: string
  city?: string
  [k: string]: unknown
}

/** One Lab / hierarchy UserLog row. */
export type UserLogApiRow = {
  id?: string
  _id?: string
  user?: { username?: string; _id?: string; id?: string }
  createdAt?: string
  ipAddress?: string
  device?: UserLogDeviceApi | null
  qualityScore?: UserLogQualityApi | null
}

/** Lab sails envelope from hierarchy-service (after CM Standard unwrap). */
export type GetUserLogResponse = {
  value: boolean
  data: {
    max?: number
    data?: UserLogApiRow[]
    count?: number
  }
  error?: string
}

export type LoginHistoryRow = {
  id: string
  name: string
  at: string
  ip: string
  device: string
  browser: string
  os: string
  isp: string
  city: string
  /** Lab IpQualityScore subset — IP detail modal. */
  qualityScore?: Record<string, unknown>
}

export type LoginHistoryResult = {
  rows: LoginHistoryRow[]
  total: number
  page: number
  pageSize: number
}

export type MemberBlockedDeviceIdResponse = {
  value: boolean
  data?: { msg?: string; blocked?: number; deviceIds?: string[] }
  error?: string
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Normalize CM-unwrapped payload into Lab getuserLog envelope. */
export function asGetUserLogResponse(live: unknown): GetUserLogResponse | null {
  if (!isRecord(live)) return null
  if (typeof live.value === 'boolean' && isRecord(live.data)) {
    return live as GetUserLogResponse
  }
  // Already the inner `{ max, data, count }` page object
  if (Array.isArray(live.data) || typeof live.count === 'number') {
    return {
      value: true,
      data: {
        max: typeof live.max === 'number' ? live.max : undefined,
        data: Array.isArray(live.data) ? (live.data as UserLogApiRow[]) : [],
        count: typeof live.count === 'number' ? live.count : 0,
      },
    }
  }
  return null
}

export function asBlockedDeviceResponse(live: unknown): MemberBlockedDeviceIdResponse {
  if (!isRecord(live)) return { value: false, error: 'Empty response' }
  if (typeof live.value === 'boolean') {
    return {
      value: live.value,
      data: isRecord(live.data)
        ? (live.data as MemberBlockedDeviceIdResponse['data'])
        : undefined,
      error: typeof live.error === 'string' ? live.error : undefined,
    }
  }
  return { value: true, data: live as MemberBlockedDeviceIdResponse['data'] }
}

export function toLoginHistoryResult(
  live: GetUserLogResponse,
  query: { page?: number; pageSize?: number } = {},
): LoginHistoryResult {
  const pageSize = live.data?.max ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = query.page ?? 1
  const total = live.data?.count ?? 0
  const rows = (live.data?.data ?? []).map(mapRow)
  return { rows, total, page, pageSize }
}

export function mapRow(row: UserLogApiRow): LoginHistoryRow {
  const device = row.device ?? null
  const quality = row.qualityScore ?? {}
  return {
    id: String(row.id ?? row._id ?? ''),
    name: String(row.user?.username ?? ''),
    at: String(row.createdAt ?? ''),
    ip: String(row.ipAddress ?? ''),
    device: formatDevice(device),
    browser: formatClient(device),
    os: formatOs(device),
    isp: String(quality.ISP ?? ''),
    city: String(quality.city ?? ''),
    qualityScore: quality as Record<string, unknown>,
  }
}

function formatDevice(device: UserLogDeviceApi | null): string {
  if (!device?.device) return ''
  const { type, brand, model } = device.device
  return [type, brand, model].filter(Boolean).join(' ').trim()
}

function formatClient(device: UserLogDeviceApi | null): string {
  if (!device?.client) return ''
  const { name, version } = device.client
  return [name, version].filter(Boolean).join(' ').trim()
}

function formatOs(device: UserLogDeviceApi | null): string {
  if (!device?.os) return ''
  const { name, version, platform } = device.os
  return [name, version, platform].filter(Boolean).join(' ').trim()
}
